import { cloneDeep } from 'lodash'

import { sortByOrder } from '../../../data/operations-tab-utils'

import { IBreak, IBreakRegistrationByDay } from '../../../../../../../models/events/event.interface'
import { convertTime } from '../../../../../../../helpers/time'
import { IRegistrationByDayInterface } from '../../../../../../../models/registrations-by-day/registrationByDay.interface'
import { RegistrationFeesType } from '../../../../../../../models/event-fees/event-fees.interface'

/**
 * Adds an increment time to a base ride time to calculate the next scheduled time.
 * Used for cascading ride time calculations in horse show scheduling.
 *
 * @param {string} rideTime - The base ride time in HH:MM:SS format (e.g., "08:30:15")
 * @param {string} incrementTime - The time increment to add in HH:MM:SS format (e.g., "00:03:30")
 * @returns {string} The resulting time in HH:MM:SS format after adding the increment
 *
 * @example
 * Calculate next rider's time with 3.5 minute increment
 * addTimeToRideTime("08:30:15", "00:03:30") // Returns "08:33:45"
 *
 * @example
 * Handle time that crosses hour boundary
 * addTimeToRideTime("11:45:00", "00:15:00") // Returns "12:00:00"
 *
 * @example
 * Handle time that crosses midnight (24-hour wrap)
 * addTimeToRideTime("23:50:00", "00:20:00") // Returns "00:10:00"
 */
const addTimeToRideTime = (rideTime: string, incrementTime: string): string => {
  const [rideHours, rideMinutes, rideSeconds] = rideTime.split(':').map(Number)
  const [incHours, incMinutes, incSeconds] = incrementTime.split(':').map(Number)

  let totalSeconds =
    rideHours * 3600 +
    rideMinutes * 60 +
    rideSeconds +
    (incHours * 3600 + incMinutes * 60 + incSeconds)

  const hours = Math.floor(totalSeconds / 3600) % 24
  totalSeconds %= 3600
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Recalculates all ride times based on the first rider's time, excluding out-of-turn riders.
 * Out-of-turn riders (order = 0, isOutOfTurn = true) maintain their manually set times.
 *
 * @param {IBreakRegistrationByDay[]} stagedData - The current staged data
 * @param {string} firstRiderTime - The first rider's time in HH:MM:SS format
 * @returns {IBreakRegistrationByDay[]} Updated staged data with recalculated times
 */
export const recalculateAllRideTimes = (
  stagedData: IBreakRegistrationByDay[],
  firstRiderTime: string
): IBreakRegistrationByDay[] => {
  const newData = [...stagedData]

  // separate OT riders from regular riders and breaks
  const outOfTurnRiders = newData.filter((item) => item.isOutOfTurn && item.order === 0)
  const inTurnItems = newData.filter((item) => !item.isOutOfTurn || item.order > 0)

  // sort in turn items by order for proper calc sequence
  const sortedInTurnItems = sortByOrder(inTurnItems)

  let currentTime = firstRiderTime
  let riderCount = 0 // track actual riders - not breaks for first rider logic

  for (let i = 0; i < sortedInTurnItems.length; i++) {
    const item = sortedInTurnItems[i]

    if (item.isBreakDrag) {
      // for breaks - add the break time to current time
      if (item.time) currentTime = addTimeToRideTime(currentTime, item.time)
      // breaks don't have ride times - continue to next
      continue
    }

    // for in turn riders
    if (riderCount === 0) {
      // first in turn rider gets the manually entered time
      item.rideTime = firstRiderTime
    } else {
      // subsequent in turn riders get calc time
      item.rideTime = currentTime
    }

    // calc the next time by adding this riders class increment
    const riderIncrement = item.fee?.increment || '00:00:00'
    currentTime = addTimeToRideTime(item.rideTime, riderIncrement)

    riderCount++
  }

  // combine the calculated in turn riders with unchanged out of turn riders
  const finalData = [...sortedInTurnItems, ...outOfTurnRiders]

  // sort the final result by orig arr order or by a combo of order and OT status
  return sortByOrder(finalData)
}

/**
 * Converts time string (HH:MM:SS) to total minutes for easier comparison
 *
 * @param {string} timeString - Time in HH:MM:SS format
 * @returns {number} Total minutes
 *
 * @example
 * timeToMinutes("01:30:45") // Returns 90.75 (90 minutes + 45 seconds / 60)
 */
export const timeToMinutes = (timeString: string): number => {
  const [hours, minutes, seconds] = timeString.split(':').map(Number)
  return hours * 60 + minutes + seconds / 60
}

/**
 * Calculates gap time conflicts for riders on the same day.
 * Identifies riders who have multiple rides with insufficient gap time between them.
 * Returns only the clash count and array of rider IDs for UI highlighting purposes.
 *
 * @param {IBreakRegistrationByDay[]} registrations - Array of registrations for the day (including breaks)
 * @param {string} requiredGapTime - Required gap time in HH:MM:SS format (e.g., "00:30:00" for 30 minutes)
 */
export const calculateRiderClashes = (
  registrations: IBreakRegistrationByDay[],
  requiredGapTime: string
) => {
  //
  if (!requiredGapTime || requiredGapTime === '00:00:00') {
    return []
  }

  const requiredGapMinutes = timeToMinutes(requiredGapTime)

  // filter to active riders only - exclude breaks, scratched riders, riders without ride times
  const activeRiders = registrations.filter(
    (reg) => !reg.isBreakDrag && !reg.isScratched && reg.rideTime && reg.horseId
  )

  const groupedByHorseIds: { [key: string]: IBreakRegistrationByDay[] } = {}

  activeRiders.forEach((item) => {
    if (item.horseId) {
      if (!groupedByHorseIds[item.horseId]) {
        groupedByHorseIds[item.horseId] = []
      }
      groupedByHorseIds[item.horseId].push(item)
    }
  })

  const clashingRiderIds = new Set<string>()

  // check each rider group for timing conflicts
  Object.keys(groupedByHorseIds).forEach((horseId) => {
    // sort riders rides by time to check
    const sortedRides = groupedByHorseIds[horseId].sort(
      (a, b) => timeToMinutes(a.rideTime!) - timeToMinutes(b.rideTime!)
    )

    if (sortedRides.length > 1) {
      // check consecutive rides for insufficient gaps
      for (let i = 0; i < sortedRides.length - 1; i++) {
        const currentTime = timeToMinutes(sortedRides[i].rideTime!)
        const nextTime = timeToMinutes(sortedRides[i + 1].rideTime!)
        const actualGap = nextTime - currentTime

        if (actualGap < requiredGapMinutes) {
          clashingRiderIds.add(horseId)
        }
      }
    }
  })

  return [...clashingRiderIds]
}

export const hasClash = (horseIds: string[], clashedIds: string[]) => {
  return clashedIds?.some((id) => horseIds.includes(id))
}

export const getRidersByRideTimeByClass = (
  data: (IRegistrationByDayInterface | IBreak)[],
  fee: RegistrationFeesType | null,
  increment: string
) => {
  let allRiders = cloneDeep(data) as IBreakRegistrationByDay[]
  allRiders = sortByOrder(allRiders)
  let newIncrement = '00:00:00'
  allRiders = allRiders.map((item) => {
    if (item.isBreakDrag) {
      newIncrement = addTimeToRideTime(newIncrement || '00:00:00', item.time)
      return item
    } else {
      const time = convertTime(
        `${fee?.startTimeHours}:${fee?.startTimeMinutes} ${fee?.startTimeFormat}`,
        newIncrement
      )
      newIncrement = addTimeToRideTime(newIncrement || '00:00:00', increment || '00:00:00')
      return { ...item, rideTimeScheduleByClass: time }
    }
  })
  return allRiders
}
